// src/features/monetize/components/LedgerActionModals.tsx
//
// The four actions on a viewer's ledger that need a reason: revoke, extend,
// refund, cancel. Each is a small modal with a form; the reason lands in
// the viewer's history.

import { Button, Checkbox, Group, Modal, NumberInput, SegmentedControl, Stack, Text, TextInput } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useController, useForm, useWatch, type Control, type FieldValues, type Path } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { DateTimeField } from '@/shared/components/DateTimeField';
import { MONETIZE_CONSTRAINTS } from '@/lib/constants/monetize';
import type { MonetizeEntitlement, MonetizePurchase, MonetizeSubscription } from '@/lib/types/Monetize';
import { MonetizeUtils } from '../MonetizeUtils';
import {
  cancelFormSchema,
  extendFormSchema,
  reasonFormSchema,
  refundFormSchema,
  type CancelFormValues,
  type ExtendFormValues,
  type ReasonFormValues,
  type RefundFormValues,
} from '../schemas/monetizeSchemas';
import { useCancelSubscription, useExtendEntitlement, useRefundPurchase, useRevokeEntitlement } from '../useMonetizeMutations';

function ReasonField<T extends FieldValues>({ control, name, placeholder }: { control: Control<T>; name: Path<T>; placeholder: string }) {
  const { field, fieldState } = useController({ control, name });
  return (
    <TextInput
      label="Reason"
      withAsterisk
      description="Appears in the viewer's history"
      placeholder={placeholder}
      maxLength={MONETIZE_CONSTRAINTS.REASON_MAX}
      value={field.value as string}
      onChange={field.onChange}
      onBlur={field.onBlur}
      error={fieldState.error?.message}
      data-autofocus
    />
  );
}

function Footer({ onClose, pending, confirm, danger }: { onClose: () => void; pending: boolean; confirm: string; danger?: boolean }) {
  return (
    <Group justify="flex-end" mt="md">
      <Button variant="default" onClick={onClose} disabled={pending}>Cancel</Button>
      <Button type="submit" color={danger ? 'red' : undefined} loading={pending}>{confirm}</Button>
    </Group>
  );
}

export function RevokeModal({ viewerId, entitlement, label, onClose }: { viewerId: string; entitlement: MonetizeEntitlement; label: string; onClose: () => void }) {
  const revoke = useRevokeEntitlement();
  const form = useForm<ReasonFormValues>({ resolver: zodResolver(reasonFormSchema), defaultValues: { reason: '' } });
  async function submit(values: ReasonFormValues) {
    try {
      await revoke.mutateAsync({ viewerId, entitlementId: entitlement.id, reason: values.reason.trim() });
      notifications.show({ title: 'Access revoked', message: `${label} no longer plays for this viewer.`, color: 'green' });
      onClose();
    } catch (err) {
      notifications.show({ title: 'Error', message: MonetizeUtils.errorMessage(err, 'Could not revoke the access.'), color: 'red' });
    }
  }
  return (
    <Modal opened onClose={onClose} title="Revoke access" centered>
      <form onSubmit={form.handleSubmit(submit)}>
        <Stack gap="md">
          <Text size="sm">
            <strong>{label}</strong> stops playing for this viewer at once. A revoked row never comes back; grant access again if you change your mind. Nothing is refunded.
          </Text>
          <ReasonField control={form.control} name="reason" placeholder="Shared account" />
          <Footer onClose={onClose} pending={revoke.isPending} confirm="Revoke access" danger />
        </Stack>
      </form>
    </Modal>
  );
}

export function ExtendModal({ viewerId, entitlement, label, onClose }: { viewerId: string; entitlement: MonetizeEntitlement; label: string; onClose: () => void }) {
  const extend = useExtendEntitlement();
  const form = useForm<ExtendFormValues>({
    resolver: zodResolver(extendFormSchema),
    defaultValues: { endsAt: MonetizeUtils.isoToPicker(entitlement.endsAt) ?? '', reason: '' },
  });
  const endsAt = useController({ control: form.control, name: 'endsAt' });
  async function submit(values: ExtendFormValues) {
    const iso = MonetizeUtils.pickerToIso(values.endsAt);
    if (!iso) return;
    try {
      await extend.mutateAsync({ viewerId, entitlementId: entitlement.id, endsAt: iso, reason: values.reason.trim() });
      notifications.show({ title: 'Access extended', message: `${label} now ends ${MonetizeUtils.formatWhen(iso)}.`, color: 'green' });
      onClose();
    } catch (err) {
      notifications.show({ title: 'Error', message: MonetizeUtils.errorMessage(err, 'Could not extend the access.'), color: 'red' });
    }
  }
  return (
    <Modal opened onClose={onClose} title="Extend access" centered>
      <form onSubmit={form.handleSubmit(submit)}>
        <Stack gap="md">
          <Text size="sm">
            <strong>{label}</strong> currently ends {entitlement.endsAt ? MonetizeUtils.formatWhen(entitlement.endsAt) : 'never'}. Pick the new end; an expired row comes back when the new end is in the future.
          </Text>
          <DateTimeField label="Ends" withAsterisk valueFormat="MMM D, YYYY h:mm A" value={endsAt.field.value} onChange={(v) => endsAt.field.onChange(v || '')} error={endsAt.fieldState.error?.message} />
          <ReasonField control={form.control} name="reason" placeholder="Stream outage on the night" />
          <Footer onClose={onClose} pending={extend.isPending} confirm="Extend access" />
        </Stack>
      </form>
    </Modal>
  );
}

export function RefundModal({ viewerId, purchase, productName, onClose }: { viewerId: string; purchase: MonetizePurchase; productName: string; onClose: () => void }) {
  const refund = useRefundPurchase();
  const form = useForm<RefundFormValues>({ resolver: zodResolver(refundFormSchema(purchase.refundable)), defaultValues: MonetizeUtils.defaultRefundForm() });
  const mode = useController({ control: form.control, name: 'mode' });
  const amount = useController({ control: form.control, name: 'amount' });
  const partial = useWatch({ control: form.control, name: 'mode' }) === 'partial';
  const refundable = MonetizeUtils.formatAmount(purchase.refundable, purchase.currency);
  async function submit(values: RefundFormValues) {
    try {
      const res = await refund.mutateAsync({ viewerId, purchaseId: purchase.id, req: MonetizeUtils.formToRefundReq(values) });
      const p = res.purchase;
      notifications.show({ title: 'Refunded', message: p.status === 'refunded' ? `${productName} refunded in full; its access is removed.` : `${MonetizeUtils.formatAmount(p.refundedAmount, p.currency)} refunded so far; access stays.`, color: 'green' });
      onClose();
    } catch (err) {
      notifications.show({ title: 'Refund failed', message: MonetizeUtils.errorMessage(err, 'The refund did not go through.'), color: 'red', autoClose: 10000 });
    }
  }
  return (
    <Modal opened onClose={onClose} title={`Refund ${productName}`} centered>
      <form onSubmit={form.handleSubmit(submit)}>
        <Stack gap="md">
          <Text size="sm">
            Up to <strong>{refundable}</strong> goes back to the card the viewer paid with, usually within 5 to 10 days. A full refund removes the access this purchase gave; a partial refund keeps it. The platform fee on the refunded part comes back to you.
          </Text>
          <SegmentedControl data={[{ value: 'full', label: `Refund ${refundable}` }, { value: 'partial', label: 'Refund part' }]} value={mode.field.value} onChange={mode.field.onChange} />
          {partial && (
            <NumberInput label="Amount" prefix="$" decimalScale={2} fixedDecimalScale min={0.01} max={purchase.refundable / 100} value={amount.field.value} onChange={amount.field.onChange} error={amount.fieldState.error?.message} maw={200} />
          )}
          <ReasonField control={form.control} name="reason" placeholder="Event canceled" />
          <Footer onClose={onClose} pending={refund.isPending} confirm="Refund" danger />
        </Stack>
      </form>
    </Modal>
  );
}

export function CancelModal({ viewerId, subscription, productName, onClose }: { viewerId: string; subscription: MonetizeSubscription; productName: string; onClose: () => void }) {
  const cancel = useCancelSubscription();
  const form = useForm<CancelFormValues>({ resolver: zodResolver(cancelFormSchema), defaultValues: MonetizeUtils.defaultCancelForm() });
  const when = useController({ control: form.control, name: 'when' });
  const refundLast = useController({ control: form.control, name: 'refundLastPayment' });
  const periodEnd = subscription.currentPeriodEnd ? MonetizeUtils.formatWhen(subscription.currentPeriodEnd) : 'the period end';
  async function submit(values: CancelFormValues) {
    try {
      await cancel.mutateAsync({ viewerId, subscriptionId: subscription.id, req: MonetizeUtils.formToCancelReq(values) });
      notifications.show({
        title: 'Subscription canceled',
        message: values.when === 'period_end' ? `${productName} ends ${periodEnd}; access stays until then.` : `${productName} ended; its access is removed.`,
        color: 'green',
      });
      onClose();
    } catch (err) {
      notifications.show({ title: 'Cancel failed', message: MonetizeUtils.errorMessage(err, 'The cancellation did not go through.'), color: 'red', autoClose: 10000 });
    }
  }
  return (
    <Modal opened onClose={onClose} title={`Cancel ${productName}`} centered>
      <form onSubmit={form.handleSubmit(submit)}>
        <Stack gap="md">
          <SegmentedControl
            data={[{ value: 'period_end', label: `At the period end (${periodEnd})` }, { value: 'now', label: 'Now' }]}
            value={when.field.value}
            onChange={when.field.onChange}
            fullWidth
          />
          <Text size="sm" c="dimmed">
            {when.field.value === 'period_end' ? 'The viewer keeps what they paid for and is not charged again.' : 'Access ends at once and the viewer is not charged again.'}
          </Text>
          <Checkbox
            label="Refund the last payment"
            description="Goes back to the viewer's card; the refund on its own does not change access, the cancellation does."
            checked={refundLast.field.value}
            onChange={(e) => refundLast.field.onChange(e.currentTarget.checked)}
          />
          <ReasonField control={form.control} name="reason" placeholder="Viewer asked by e-mail" />
          <Footer onClose={onClose} pending={cancel.isPending} confirm="Cancel subscription" danger />
        </Stack>
      </form>
    </Modal>
  );
}
